import { Badge, Box, Mixins, Text } from '@openfin/ui-library';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { NotificationIndicator, IndicatorColor } from '../../../../client/indicator';
import { SectionAlignment } from '../../../../client/templates/templates';
import { useTooltip } from '../../hooks/useTooltip';

interface Props {
  indicator: NotificationIndicator;
  alignment: SectionAlignment;
  stackSize?: number;
}

export const IndicatorBanner: React.FC<Props> = (props) => {
  const { indicator, children, alignment, stackSize = 0 } = props;
  const axis = alignment === 'center' ? IndicatorAxis.horizontal : IndicatorAxis.vertical;
  const text = indicator.text || indicator.type;
  const tooltip = useTooltip<HTMLDivElement>(text);

  return (
    <axis.Container
      className={`indicator`}
      indicatorColor={indicator.color}
      alignItems="center"
      key={`${alignment}-indicator`}
    >
      {stackSize > 0 && (
        <axis.CountWrapper>
          <axis.StackCount
            count={stackSize}
            max={99}
            alignment={alignment}
            aria-label="Stack count"
            aria-valuenow={stackSize}
            data-testid="count"
          />
        </axis.CountWrapper>
      )}
      <axis.TextWrapper shift={stackSize > 0}>
        <axis.Message ref={tooltip} alignment={alignment} data-testid="text">
          {children || text}
        </axis.Message>
      </axis.TextWrapper>
    </axis.Container>
  );
};

export const InfoBanner = ({ text }: { text: string }): JSX.Element => {
  return <IndicatorBanner indicator={{ text, color: IndicatorColor.GRAY }} alignment="center" />;
};

const Color = css<{ indicatorColor: IndicatorColor | undefined }>`
  background: ${({ indicatorColor, theme }) => {
    switch (indicatorColor) {
      case IndicatorColor.GREEN:
        return theme.palette.statusSuccess;
      case IndicatorColor.YELLOW:
        return theme.palette.statusWarning;
      case IndicatorColor.RED:
        return theme.palette.statusCritical;
      case IndicatorColor.BLUE:
        return theme.palette.statusActive;
      case IndicatorColor.PURPLE:
        return '#8b46ff';
      case IndicatorColor.GRAY:
        return theme.palette.background2;
      default:
        return 'transparent';
    }
  }};
`;

const IndicatorAxis = {
  horizontal: {
    CountWrapper: styled(Box)`
      min-width: ${(props) => props.theme.px.xxxxlarge};
      width: ${(props) => props.theme.px.xxxxlarge};
      z-index: 100;
    `,
    TextWrapper: styled(Box)<{ shift?: boolean }>`
      width: ${({ shift, theme }) => (shift ? `calc(100% - ${theme.px.xxxxlarge})` : '100%')};
      & > * {
        width: ${({ shift, theme }) =>
          shift ? `calc(100% - ${theme.px.xxlarge})` : '100%'} !important;
        margin-left: 0;
        &:not([title]) {
          margin-left: -${({ shift, theme }) => (shift ? theme.unit.xxlarge / 2 + 'px' : '0')};
        }
      }
    `,
    StackCount: styled(Badge)<{ alignment: SectionAlignment }>`
      background: rgba(0, 0, 0, 0.25);
      border-radius: ${(props) => props.theme.radius.small};
      justify-self: flex-start;
      box-shadow: none;
    `,
    Message: styled(Text)<{ alignment: SectionAlignment }>`
      text-align: center;
      width: 100%;
      text-transform: uppercase;
      ${Mixins.textOverflow};
    `,
    Container: styled(Box)<{ indicatorColor: IndicatorColor | undefined }>`
      user-select: none;
      position: relative;
      width: 100%;
      height: ${(props) => props.theme.px.xxlarge};
      padding: 0 ${(props) => props.theme.px.small};
      ${Color}
    `,
  },
  vertical: {
    CountWrapper: styled(Box)`
      z-index: 100;
    `,
    TextWrapper: styled(Box)<{ shift?: boolean }>`
      justify-content: center;
      flex: 1;
      width: ${({ theme }) => theme.px.xlarge};
      writing-mode: vertical-rl;
      padding-top: ${({ theme }) => theme.px.xsmall};
      overflow: hidden;
    `,

    Message: styled(Text)<{ alignment: SectionAlignment }>`
      transform: ${({ alignment }) => (alignment === 'left' ? 'rotate(180deg)' : '')};
      text-align: center;
      text-transform: uppercase;
      ${Mixins.textOverflow};
    `,

    StackCount: styled(Badge)<{ alignment: SectionAlignment }>`
      background: rgba(0, 0, 0, 0.25);
      border-radius: ${({ theme }) => theme.radius.small};
      box-shadow: none;
      align-self: center;
      // next 3 values are not in theme.px
      margin-left: ${({ alignment }) => (alignment === 'left' ? '1px' : '0')};
      margin-right: ${({ alignment }) => (alignment === 'right' ? '1px' : '0')};
      width: 22px;
    `,

    Container: styled(Box)<{ indicatorColor: IndicatorColor | undefined }>`
      user-select: none;
      position: relative;
      width: ${({ theme }) => theme.px.xxlarge};
      height: 100%;
      padding: ${({ theme }) => `${theme.px.small} ${theme.px.xsmall}`};
      flex-direction: column;
      ${Color}
    `,
  },
};
